import { useMemo } from 'react'
import styled from '@emotion/styled'

import { ReactComponent as DefuserIcon } from 'src/assets/icons/defuser.svg'
import type { PendingMatchStatus } from 'src/graphql/types'
import { useProcessing } from 'src/screens/matches/collection/match/status/use-progress'
import { washColor } from 'src/screens/matches/collection/match/status/wash-color'

// Values lifted verbatim from the CS2 defuse HUD (CSGOHudProgressBar):
// the arc grows from the bottom and meets at the top, washed one colour at a
// time from red -> yellow (50%) -> green, with the CT-blue defuser in the middle.
const size = 92
const ctBlue = '#B5D4EE'
const ring =
  'radial-gradient(closest-side, transparent 0 80%, #000 82% 98%, transparent 100%)'

export function DefuserProgress(props: {
  createdAt: string
  status: PendingMatchStatus
  className?: string
}) {
  const { progress, motionPaused, stalled } = useProcessing(
    props.createdAt,
    props.status
  )

  const arc = useMemo(() => {
    const start = 180 - 180 * progress
    const sweep = 360 * progress
    // Stalled: hold the fill where it parked but wash it back to the start red.
    const color = stalled ? washColor(0) : washColor(progress)
    return `conic-gradient(from ${start}deg, ${color} 0deg ${sweep}deg, transparent ${sweep}deg 360deg)`
  }, [progress, stalled])

  return (
    <Dial className={props.className}>
      <Scrim />
      <Track />
      <Arc style={{ background: arc }} />
      <IconWrap paused={motionPaused}>
        <DefuserIcon />
      </IconWrap>
    </Dial>
  )
}

const Dial = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${size}px;
  height: ${size}px;
  z-index: 0;
  pointer-events: none;
`

// Darkens only the centre behind the icon; fades out well inside the ring so
// it never draws a visible edge next to it.
const Scrim = styled.div`
  position: absolute;
  inset: 6%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(10, 13, 17, 0.5), transparent 66%);
`

const Layer = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
`

const Track = styled(Layer)`
  background: rgba(128, 128, 128, 0.25);
  -webkit-mask: ${ring};
  mask: ${ring};
`

const Arc = styled(Layer)`
  -webkit-mask: ${ring};
  mask: ${ring};
`

const IconWrap = styled.div<{ paused: boolean }>`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 40px;
  height: 36px;
  color: ${ctBlue};
  filter: drop-shadow(1px 1px 2px #000);
  animation: defuser-pulse 0.8s ease-in-out infinite;
  animation-play-state: ${(props) => (props.paused ? 'paused' : 'running')};

  svg {
    display: block;
    width: 100%;
    height: 100%;
    fill: currentColor;
  }

  @keyframes defuser-pulse {
    0% {
      filter: brightness(1) drop-shadow(1px 1px 2px #000);
      transform: translate(-50%, -50%) scale(0.95);
    }
    50% {
      filter: brightness(1.5) drop-shadow(1px 1px 2px #000);
    }
    90% {
      transform: translate(-50%, -50%) scale(1);
    }
    100% {
      filter: brightness(1) drop-shadow(1px 1px 2px #000);
      transform: translate(-50%, -50%) scale(0.95);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: translate(-50%, -50%);
  }
`
